// PRST-134 — turns the flat proc rowset into the per-month shape the PDF prints.
//
// Pure on purpose: no React, no @react-pdf/renderer. The business invariant this report lives or
// dies by ("the sum of the checks listed and the totals provided are equal") is asserted here, in
// a module that unit-tests without a single mock. Putting the sign convention or the totalling
// inside the document component would bury a business rule in a file that can only be tested
// through a mocked renderer.
//
// Transport contract (see sp_cl_cp_bi_pv): ONE flat array, every row carrying a Section
// discriminator. Not a nested envelope — getReportData() in ../operations returns [] for any JSON
// object without a truthy .length, so a { cover, checks } payload would surface to the user as
// "No data found".

import { formatIsoToDate } from '../../../utils';

export const SECTION = { COVER: 'COVER', CHECK: 'CHECK', DETAIL: 'DETAIL', RECON: 'RECON' };

// Values as the Axure mockup prints them: 'Tax' and 'Deduction' are SINGULAR in the Transaction
// Type column, even though their total lines read "Tax Total" / "Deduction Total". The proc emits
// these strings verbatim.
export const TRANSACTION_TYPES = ['Earnings', 'Tax', 'Deduction'];

// Money is summed in integer cents and divided once at the end. Accumulating floats over a few
// hundred check rows produces a 0.01 drift, which would turn the tie-out assertions below into
// approximate comparisons — and an approximate tie-out is not a tie-out on a subpoenaed document.
const toCents = value => Math.round((Number(value) || 0) * 100);
const fromCents = cents => cents / 100;

const toHours = value => Math.round((Number(value) || 0) * 10000);
const fromHours = ten_thousandths => ten_thousandths / 10000;

// Grouping is by the check PAID date, per the ticket ("Summary is based upon Check Paid date. All
// Checks paid in a calendar month will appear on that designated page") — never the period-end
// date the user searched on. A check for a December period paid in January belongs on the January
// page.
export const monthKey = checkDate => {
  if (!checkDate) return '';
  const text = String(checkDate);
  const iso = text.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const us = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (us) return `${us[3]}-${us[1]}`;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

// 'YYYY-MM' is the storage form precisely so a plain string sort orders 2025-12 before 2026-01.
// 'MM / YYYY' is the display form — two digit month, four digit year, slash-separated as the
// Axure mockup renders it ("10 / 2016"). It sorts wrongly, so the two forms are kept apart.
export const formatMonthYear = key => {
  if (!key) return '';
  const [year, month] = String(key).split('-');
  return year && month ? `${month} / ${year}` : '';
};

// Splits the flat rowset. Tolerates [] and undefined so a failed or empty report cannot throw
// before the page gets to show its own message.
export const normalizeReport = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  const cover = list.find(row => row?.Section === SECTION.COVER) || null;
  const checks = list.filter(row => row?.Section === SECTION.CHECK);
  const transactions = list.filter(row => row?.Section === SECTION.DETAIL);
  const recon = list.filter(row => row?.Section === SECTION.RECON);
  return { cover, checks, transactions, recon };
};

// The proc stamps MonthKey, but it is recomputed from CheckDate when absent so the grouping rule
// stays visible (and testable) in one place rather than being an implicit property of the SQL.
const keyOf = row => row?.MonthKey || monthKey(row?.CheckDate);

export const groupChecksByMonth = ({ checks = [], transactions = [] } = {}) => {
  const months = new Map();
  const bucket = key => {
    if (!months.has(key)) months.set(key, { key, label: formatMonthYear(key), checks: [], transactions: [] });
    return months.get(key);
  };

  checks.forEach(row => {
    const key = keyOf(row);
    if (key) bucket(key).checks.push(row);
  });
  transactions.forEach(row => {
    const key = keyOf(row);
    if (key) bucket(key).transactions.push(row);
  });

  return [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
};

const typeOrder = row => {
  const declared = Number(row?.TransactionTypeSort);
  if (Number.isFinite(declared) && declared > 0) return declared;
  const index = TRANSACTION_TYPES.indexOf(row?.TransactionType);
  return index === -1 ? TRANSACTION_TYPES.length + 1 : index + 1;
};

// Sign convention: earnings positive, taxes and deductions negative, exactly as the ticket
// specifies — but the signing belongs to the proc (sp_cl_cp_bi_pcr's CASE already does the * -1),
// and this takes the value as given.
//
// It deliberately does NOT force the sign per transaction type. Doing so looked like cheap
// insurance against a proc that forgot the * -1, and it silently destroys void reversals: a voided
// check contributes a NEGATIVE earnings row and a POSITIVE tax row, which Math.abs would flip back
// into a second set of real earnings. The month would then total twice the gross with no error.
// A mis-signed proc value is caught properly by verifyMonthTieOut below, which is what that check
// is for — one mechanism, not two fighting each other.
const signedAmount = row => toCents(row?.Amount);

export const summarizeMonth = (month = {}) => {
  const checks = month.checks || [];
  const detail = { Earnings: [], Tax: [], Deduction: [] };
  const totalCents = { Earnings: 0, Tax: 0, Deduction: 0 };
  // Hours are summed per type so the total line can carry them. The mockup shows 138.00 against
  // Earnings Total; tax and deduction rows have no hours at all, and their totals stay blank
  // rather than printing 0.00.
  const totalHours = { Earnings: 0, Tax: 0, Deduction: 0 };
  const hasHours = { Earnings: false, Tax: false, Deduction: false };

  [...(month.transactions || [])]
    .sort((a, b) => typeOrder(a) - typeOrder(b) || String(a?.TransactionDetail).localeCompare(String(b?.TransactionDetail)))
    .forEach(row => {
      const type = row?.TransactionType;
      if (!detail[type]) return;
      const cents = signedAmount(row);
      totalCents[type] += cents;
      const rowHours = (row?.Hours ?? '') === '' ? null : toHours(row.Hours);
      if (rowHours !== null) {
        totalHours[type] += rowHours;
        hasHours[type] = true;
      }
      detail[type].push({
        transactionType: type,
        transactionDetail: (row?.TransactionDetail || '').trim(),
        amount: fromCents(cents),
        hours: rowHours === null ? null : fromHours(rowHours),
      });
    });

  const grossCents = checks.reduce((sum, row) => sum + toCents(row?.GrossPay), 0);
  const netCents = checks.reduce((sum, row) => sum + toCents(row?.NetPay), 0);

  return {
    key: month.key,
    label: month.label,
    checks,
    detail,
    totals: {
      earnings: fromCents(totalCents.Earnings),
      taxes: fromCents(totalCents.Tax),
      deductions: fromCents(totalCents.Deduction),
      gross: fromCents(grossCents),
      net: fromCents(netCents),
    },
    totalHours: {
      Earnings: hasHours.Earnings ? fromHours(totalHours.Earnings) : null,
      Tax: hasHours.Tax ? fromHours(totalHours.Tax) : null,
      Deduction: hasHours.Deduction ? fromHours(totalHours.Deduction) : null,
    },
    // Kept in cents so verifyMonthTieOut compares integers rather than floats.
    cents: {
      earnings: totalCents.Earnings,
      taxes: totalCents.Tax,
      deductions: totalCents.Deduction,
      gross: grossCents,
      net: netCents,
    },
  };
};

// The two identities the business called out as "very important". They are NOT guaranteed by
// construction: gross and net are columns on the check HEADER table while the detail sums come
// from a different table, so a dropped zero-amount row, a mis-signed EIC credit or divergent void
// handling breaks them silently. The page warns on a mismatch rather than blocking — a proc bug
// must be visible in UAT, not rendered as a blank screen.
export const verifyMonthTieOut = (summary = {}) => {
  const cents = summary.cents || { earnings: 0, taxes: 0, deductions: 0, gross: 0, net: 0 };
  const grossDeltaCents = cents.earnings - cents.gross;
  // taxes and deductions are already negative, so this is Earnings - Tax - Deduction.
  const computedNetCents = cents.earnings + cents.taxes + cents.deductions;
  const netDeltaCents = computedNetCents - cents.net;
  return {
    key: summary.key,
    grossOk: grossDeltaCents === 0,
    netOk: netDeltaCents === 0,
    grossDelta: fromCents(grossDeltaCents),
    netDelta: fromCents(netDeltaCents),
  };
};

// PRST-140 — the sentence printed when the selected range holds no checks. The proc builds the
// COVER row from the employee record independently of @Checks, so a range with no period end in it
// still comes back with a full cover and the screen and the PDF looked exactly like a complete
// packet (which is how the PRST-138 filter bug passed stage UAT). Both surfaces print THIS string
// so they cannot drift. It warns; it does not block — a cover-only packet is a legitimate result.
export const emptyPeriodMessage = (criteria = {}) =>
  `No payroll checks were found for periods ending between ${formatIsoToDate(criteria.FromPerEnddate)} and ` +
  `${formatIsoToDate(criteria.ToPerEnddate)}. The export will contain the cover page only.`;

// One call for the page: flat rows in, everything the screen and the document need out.
export const buildReport = (rows = [], reportDate = new Date()) => {
  const { cover, checks, transactions, recon } = normalizeReport(rows);
  const months = groupChecksByMonth({ checks, transactions }).map(summarizeMonth);
  return { cover, months, recon, reportDate, tieOut: months.map(verifyMonthTieOut) };
};
